import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of, Subject, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ProdutoListaRefinamentoEnum } from '@shared/enums/produto/produto-lista-refinamento.enum';
import { ProdutoService } from '@shared/services/apis/produto/produto.service';
import { ProductSearchResult } from '../card-edit-dialog.types';

/**
 * Busca de produto do cadastro (campo + lista do autocomplete) dos painéis "Adicionar item" de
 * Produtos (Solicitação/Cotação) e do Pedido.
 *
 * Contrato com o painel:
 * - `itensAdicionados` são os itens já na lista: os SKUs deles ficam fora dos resultados.
 * - `(produtoSelecionado)` emite o produto escolhido; o campo já volta limpo. Cada painel monta o
 *   próprio item a partir dele.
 * - O componente vive dentro do `@if` do painel: abrir recria a busca do zero e já foca o campo.
 */
@Component({
  selector: 'app-ced-busca-produto',
  standalone: true,
  imports: [
    FormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './ced-busca-produto.component.html',
  styleUrls: ['./ced-busca-produto.component.css'],
})
export class CedBuscaProdutoComponent implements AfterViewInit, OnDestroy {
  @Input() itensAdicionados: { sku: string }[] = [];

  @Output() produtoSelecionado = new EventEmitter<ProductSearchResult>();

  @ViewChild('buscaProdutoInput') private buscaProdutoInput?: ElementRef<HTMLInputElement>;
  query = '';
  resultados: ProductSearchResult[] = [];
  buscando = false;

  private readonly busca$ = new Subject<string>();
  private readonly subs = new Subscription();

  constructor(private produtoService: ProdutoService) {
    // Sem distinctUntilChanged: buscar de novo o mesmo termo (depois de incluir um produto) precisa
    // gerar nova requisição. O switchMap cancela a busca anterior; termo curto limpa na hora, sem
    // esperar o debounce, para uma resposta atrasada não reaparecer com o campo já apagado.
    this.subs.add(
      this.busca$.pipe(
        switchMap((q) => {
          if (q.length < 2) return of(null);
          return timer(300).pipe(
            switchMap(() => {
              this.resultados = [];
              return this.produtoService.getAll({
                searchText: q,
                idProdutoListaRefinamento: ProdutoListaRefinamentoEnum.Descricao,
                isAtivo: true,
              }, 0).pipe(catchError(() => of(null)));
            }),
          );
        }),
      ).subscribe((res) => {
        this.buscando = false;
        const adicionados = new Set(this.itensAdicionados.map(i => i.sku).filter(Boolean));
        this.resultados = (!res || res.isError ? [] : (res.list || []))
          .filter(p => !adicionados.has(p.codigoSku || ''))
          .slice(0, 8)
          .map(p => ({
            idProduto: p.idProduto,
            sku: p.codigoSku || '',
            name: p.descricao,
            marca: p.marca || null,
            unit: p.unidade || 'un',
            stock: p.saldoDisponivel ?? p.saldoFisico ?? 0,
            price: p.preco ?? 0,
          }));
      }),
    );
  }

  ngAfterViewInit(): void {
    this.focar();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Foca o campo de busca (ao abrir o painel e quando o painel cancela a inclusão). */
  focar(): void {
    setTimeout(() => this.buscaProdutoInput?.nativeElement.focus());
  }

  onQueryChange(valor: string | ProductSearchResult): void {
    // Ao escolher uma opção, o autocomplete devolve o objeto ao ngModel: a escolha é tratada em selecionar().
    if (typeof valor !== 'string') return;
    this.query = valor;
    const q = valor.trim();
    // Já marca a busca durante o debounce: sem isso o "Nenhum produto encontrado" piscava antes da resposta.
    this.buscando = q.length >= 2;
    if (!this.buscando) this.resultados = [];
    this.busca$.next(q);
  }

  selecionar(r: ProductSearchResult): void {
    // O autocomplete escreve a opção escolhida no campo; limpa já para a próxima busca começar do zero.
    this.query = '';
    this.resultados = [];
    this.busca$.next('');
    this.produtoSelecionado.emit(r);
  }
}
